// src/padplanner/ruleVocabulary.js
//
// A clearance rule's words, its selectors, and the field checks the rule form
// runs before it sends (S5b). The server is the authority:
// admin-api/lib/padplanner/rules.js validates, and migration 153's CHECKs
// validate again. This is a copy of rules.js's checks, not an import (the UI
// image is built from ui/ alone). ruleVocabulary.test.js runs both on the
// same inputs and on the shared fixture's selector cases, so the copy cannot
// drift: what the form passes, the server passes, with the same code.
//
// A refusal the server still returns is shown verbatim with its code; these
// messages are only the form's own, before anything is sent.
//
// ── SELECTORS (D-S5-8) ──────────────────────────────────────────────────────
//
// Any of category (163's sixteen), template_keys, well_linked and layer; {} is
// every footprint and zone. A feature's category and key are its template's
// (a freeform feature has neither), as the engine's
// pad_plan_rule_selector_matches reads them. selectorMatches is that
// function: the editor's live distances and redzones pick features with it.
import { TEMPLATE_CATEGORIES } from "./templateVocabulary";

export const SELECTOR_CATEGORIES = TEMPLATE_CATEGORIES;
export const RULE_RELATIONS = Object.freeze(["min_distance", "max_distance", "inside_perimeter", "no_overlap"]);
export const DISTANCE_RELATIONS = Object.freeze(["min_distance", "max_distance"]);
export const RULE_SEVERITIES = Object.freeze(["block", "warn"]);
export const SELECTOR_KEYS = Object.freeze(["category", "template_keys", "well_linked", "layer"]);
export const MAX_LIMIT_FT = 2640;
export const MAX_RULES_PER_SET = 200;

export const RELATION_LABELS = Object.freeze({
  min_distance: "At least", max_distance: "At most", inside_perimeter: "Inside the perimeter", no_overlap: "No overlap",
});
export const SEVERITY_LABELS = Object.freeze({ block: "Block", warn: "Warn" });
// "min 100′" in a witness label, as mockup 4 has it.
export const LIMIT_WORDS = Object.freeze({ min_distance: "min", max_distance: "max" });

const KEY_RE = /^[a-z0-9][a-z0-9_]{0,47}$/;
const LAYER_RE = /^[a-z0-9][a-z0-9_]{0,39}$/;

/** rules.js selectorProblem: why a selector is invalid, or null. */
export function selectorProblem(sel) {
  if (sel === null || typeof sel !== "object" || Array.isArray(sel)) return "a selector is an object";
  const unknown = Object.keys(sel).filter((k) => !SELECTOR_KEYS.includes(k));
  if (unknown.length) return `unknown selector key(s): ${unknown.join(", ")}; a selector names ${SELECTOR_KEYS.join(", ")}`;
  if ("category" in sel && !SELECTOR_CATEGORIES.includes(sel.category)) {
    return `category is one of ${SELECTOR_CATEGORIES.join(", ")}`;
  }
  if ("template_keys" in sel) {
    const k = sel.template_keys;
    if (!Array.isArray(k) || k.length < 1 || k.length > 50 || !k.every((x) => typeof x === "string" && KEY_RE.test(x))) {
      return "template_keys is 1 to 50 template keys";
    }
  }
  if ("well_linked" in sel && typeof sel.well_linked !== "boolean") return "well_linked is true or false";
  if ("layer" in sel && (typeof sel.layer !== "string" || !LAYER_RE.test(sel.layer))) {
    return "layer is 1 to 40 lowercase letters, digits or underscores";
  }
  return null;
}

const refusal = (code, error, detail) => ({ code, error, ...(detail ? { detail } : {}) });

/**
 * rules.js validateRuleFields, for an add (current null) or an edit (the
 * stored rule). Returns {value} or {refusal: {code, error}}, with the
 * server's codes and words.
 */
export function validateRuleFields(body, current) {
  const adding = current === null;
  const value = {};
  const has = (k) => adding || Object.prototype.hasOwnProperty.call(body, k);

  if (has("rule_key")) {
    if (typeof body.rule_key !== "string" || !KEY_RE.test(body.rule_key)) {
      return { refusal: refusal("rule_key_invalid",
        "A rule key is 1 to 48 lowercase letters, digits or underscores, starting with a letter or digit.") };
    }
    value.rule_key = body.rule_key;
  }
  if (has("relation")) {
    if (!RULE_RELATIONS.includes(body.relation)) {
      return { refusal: refusal("rule_relation_invalid", `relation is one of ${RULE_RELATIONS.join(", ")}.`) };
    }
    value.relation = body.relation;
  }
  if (has("subject_selector")) {
    const p = selectorProblem(body.subject_selector);
    if (p) return { refusal: refusal("rule_selector_invalid", `subject_selector: ${p}.`, { field: "subject_selector" }) };
    value.subject_selector = body.subject_selector;
  }
  if (has("severity")) {
    if (!RULE_SEVERITIES.includes(body.severity)) return { refusal: refusal("rule_severity_invalid", "severity is block or warn.") };
    value.severity = body.severity;
  }
  if (has("message")) {
    if (typeof body.message !== "string" || body.message.trim().length < 1 || body.message.trim().length > 200) {
      return { refusal: refusal("rule_message_invalid", "A rule's message is 1 to 200 characters.") };
    }
    value.message = body.message.trim();
  }
  if (Object.prototype.hasOwnProperty.call(body, "sort_order")) {
    if (!Number.isInteger(body.sort_order) || body.sort_order < 0 || body.sort_order > 10000) {
      return { refusal: refusal("rule_sort_order_invalid", "sort_order is an integer from 0 to 10000.") };
    }
    value.sort_order = body.sort_order;
  }

  const relation = value.relation ?? current?.relation;
  const distance = DISTANCE_RELATIONS.includes(relation);
  const objectSel = Object.prototype.hasOwnProperty.call(body, "object_selector") ? body.object_selector
    : adding ? undefined : current.object_selector;
  const limit = Object.prototype.hasOwnProperty.call(body, "limit_ft") ? body.limit_ft
    : adding ? undefined : current.limit_ft;

  if (relation === "inside_perimeter") {
    if (objectSel !== undefined && objectSel !== null) {
      return { refusal: refusal("rule_object_not_allowed", "inside_perimeter has no object: it checks the subject against the plan's perimeter.") };
    }
  } else {
    const p = objectSel === undefined || objectSel === null ? "an object_selector is required for this relation" : selectorProblem(objectSel);
    if (p) return { refusal: refusal("rule_selector_invalid", `object_selector: ${p}.`, { field: "object_selector" }) };
  }
  if (distance) {
    if (typeof limit !== "number" || !Number.isFinite(limit) || limit <= 0 || limit > MAX_LIMIT_FT
        || Math.round(limit * 100) !== limit * 100) {
      return { refusal: refusal("rule_limit_invalid", `limit_ft is more than 0 and at most ${MAX_LIMIT_FT} ft, to the hundredth.`) };
    }
  } else if (limit !== undefined && limit !== null) {
    return { refusal: refusal("rule_limit_not_allowed", `${relation} has no limit; only min_distance and max_distance do.`) };
  }
  if (adding || "relation" in value || Object.prototype.hasOwnProperty.call(body, "object_selector")) {
    value.object_selector = relation === "inside_perimeter" ? null : objectSel;
  }
  if (adding || "relation" in value || Object.prototype.hasOwnProperty.call(body, "limit_ft")) {
    value.limit_ft = distance ? limit : null;
  }
  return { value };
}

/**
 * The engine's pad_plan_rule_selects (156), for one feature: its template's
 * category and key, its well link and its layer. A line is selected only by
 * a selector that names its layer or category "line" (its category is
 * "line"), so {} and every other selector select footprints and zones only,
 * as under 153. kind absent reads as a footprint (the fixture's features).
 */
export function selectorMatches(sel, { kind = "footprint", category = null, key = null, well = null, layer = null }) {
  if (!sel || typeof sel !== "object") return false;
  if (!["footprint", "zone", "line"].includes(kind)) return false;
  if (kind === "line") {
    if (!("layer" in sel || sel.category === "line")) return false;
    category = "line";
  }
  if ("category" in sel && category !== sel.category) return false;
  if ("template_keys" in sel && !(Array.isArray(sel.template_keys) && key !== null && sel.template_keys.includes(key))) return false;
  if ("well_linked" in sel && (well !== null) !== sel.well_linked) return false;
  if ("layer" in sel && layer !== sel.layer) return false;
  return true;
}

/** What a selector reads off a feature, given its template (or none). */
export function featureFacts(feature, template) {
  return {
    kind: feature.kind ?? "footprint",
    category: template?.category ?? (feature.kind === "line" ? "line" : null),
    key: template?.key ?? null,
    well: feature.well_api10 ?? null,
    layer: feature.layer ?? null,
  };
}

/** "Wellheads with a well · layer flowback"; {} is "Any footprint or zone" (a line is selected only by name, 156). */
export function selectorText(sel, categoryLabels = {}) {
  if (!sel || typeof sel !== "object") return "—";
  const parts = [];
  if ("category" in sel) parts.push(categoryLabels[sel.category] ?? sel.category);
  if ("template_keys" in sel) parts.push(sel.template_keys.join(", "));
  if ("well_linked" in sel) parts.push(sel.well_linked ? "linked to a well" : "not linked to a well");
  if ("layer" in sel) parts.push(`layer ${sel.layer}`);
  return parts.length ? parts.join(" · ") : "Any footprint or zone";
}

/** "At least 100′ from", "Inside the perimeter": the relation and its limit. */
export function relationText(rule) {
  if (DISTANCE_RELATIONS.includes(rule.relation)) return `${RELATION_LABELS[rule.relation]} ${formatFt(rule.limit_ft)} from`;
  return RELATION_LABELS[rule.relation] ?? rule.relation;
}

/** 100 -> "100′", 12.5 -> "12.5′". */
export function formatFt(ft) {
  if (ft === null || ft === undefined || !Number.isFinite(Number(ft))) return "—";
  return `${Number(Number(ft).toFixed(2))}′`;
}

/** A refusal as the server sent it: its words, verbatim, and its code. */
export function serverRefusal(e) {
  if (!e) return null;
  return `${e.message || "Something went wrong."}${e.code ? ` (${e.code})` : e.status ? ` (${e.status})` : ""}`;
}

/** The rule form's picker state for a selector. */
export const fromSelector = (sel) => ({
  category: sel?.category ?? "", keys: sel?.template_keys ?? [],
  well: "well_linked" in (sel ?? {}) ? String(sel.well_linked) : "", layer: sel?.layer ?? "",
});

/** The picker state as a selector. Empty pickers are left out: {} selects any footprint or zone. */
export function toSelector(v) {
  const sel = {};
  if (v.category) sel.category = v.category;
  if (v.keys.length) sel.template_keys = v.keys;
  if (v.well !== "") sel.well_linked = v.well === "true";
  if (v.layer.trim()) sel.layer = v.layer.trim();
  return sel;
}
